import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, BarChart3, PieChart, TrendingUp, MapPin, Target,
  Download, RefreshCw, Lock, Eye, EyeOff, Filter, Calendar,
  Lightbulb, CheckCircle, ArrowLeft, FileText, Table, Image,
  FileDown, Printer, Globe, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import {
  LineChart, Line, BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ComposedChart, Area, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import BelgiumMap from '../components/BelgiumMap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Admin password
const ADMIN_PASSWORD = "declic2025";

// Color palette
const COLORS = ['#A4187A', '#FB6823', '#2196F3', '#4CAF50', '#E91E63', '#9C27B0', '#00BCD4', '#FFC107', '#795548', '#607D8B'];
const DOMAIN_COLORS = {
  'Santé': '#4CAF50',
  'Technologie': '#2196F3',
  'Créativité': '#E91E63',
  'Social': '#FB6823',
  'Éducation': '#9C27B0',
  'Business': '#795548',
  'Science': '#00BCD4',
  'Technique': '#607D8B',
  'Gestion & Organisation': '#FFC107',
  'Sport & Bien-être': '#F44336'
};

export default function AnalyticsDashboard() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardLang, setDashboardLang] = useState('fr'); // Language selector
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // PART 3: Sorting state
  const [sortConfig, setSortConfig] = useState({
    careers: { key: 'count', direction: 'desc' },
    domains: { key: 'count', direction: 'desc' },
    crossAnalysis: { key: 'count', direction: 'desc' },
    accessibility: { key: 'score', direction: 'asc' }
  });
  
  // PART 7: Filters state
  const [filters, setFilters] = useState({
    ageGroup: 'all',
    gender: 'all',
    region: 'all',
    domain: 'all',
    dateRange: { start: null, end: null }
  });
  
  // Educational impact data
  const [educationalImpact, setEducationalImpact] = useState(null);
  
  // Chart refs for export
  const chartsRef = useRef(null);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setAuthError('');
      fetchData(dashboardLang);
    } else {
      setAuthError(dashboardLang === 'fr' ? 'Mot de passe incorrect' : 'Onjuist wachtwoord');
    }
  };

  const fetchData = async (lang = dashboardLang) => {
    setLoading(true);
    setIsRefreshing(true);
    setError(null);
    try {
      // Fetch main analytics data with interpretations (PART 1 & 2)
      const response = await axios.get(`${API}/analytics/with-interpretations?lang=${lang}`);
      setData(response.data);
      setLastUpdated(new Date());
      
      // Fetch enhanced educational impact data
      try {
        const impactResponse = await axios.get(`${API}/analytics/educational-impact?lang=${lang}`);
        setEducationalImpact(impactResponse.data);
      } catch (impactErr) {
        console.warn('Educational impact data not available:', impactErr);
      }
    } catch (err) {
      // Fallback to regular endpoint if interpretations endpoint fails
      try {
        const fallbackResponse = await axios.get(`${API}/analytics/advanced?lang=${lang}`);
        setData(fallbackResponse.data);
        setLastUpdated(new Date());
      } catch (fallbackErr) {
        setError(err.response?.data?.detail || 'Failed to load analytics');
        console.error('Error fetching analytics:', err);
      }
    }
    setLoading(false);
    setIsRefreshing(false);
  };

  // Manual refresh handler (PART 2)
  const handleManualRefresh = () => {
    if (!isRefreshing) {
      fetchData(dashboardLang);
    }
  };

  // Change language and refetch data
  const handleLanguageChange = (newLang) => {
    setDashboardLang(newLang);
    if (isAuthenticated) {
      fetchData(newLang);
    }
  };
  
  // PART 3: Sorting functions
  const handleSort = (table, key) => {
    setSortConfig(prev => ({
      ...prev,
      [table]: {
        key,
        direction: prev[table].key === key && prev[table].direction === 'desc' ? 'asc' : 'desc'
      }
    }));
  };
  
  const getSortedData = (data, table) => {
    if (!data || !Array.isArray(data)) return data;
    const { key, direction } = sortConfig[table] || { key: 'count', direction: 'desc' };
    return [...data].sort((a, b) => {
      const aVal = a[key] ?? 0;
      const bVal = b[key] ?? 0;
      if (direction === 'asc') return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });
  };
  
  const SortIcon = ({ table, column }) => {
    const { key, direction } = sortConfig[table] || {};
    if (key !== column) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="text-[#A4187A] ml-1">{direction === 'asc' ? '↑' : '↓'}</span>;
  };
  
  // PART 7: Filter handler
  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({ ...prev, [filterKey]: value }));
  };
  
  const resetFilters = () => {
    setFilters({
      ageGroup: 'all',
      gender: 'all',
      region: 'all',
      domain: 'all',
      dateRange: { start: null, end: null }
    });
  };
  
  // Belgian regions mapping (normalize different spellings)
  const BELGIAN_REGIONS = {
    'Brussels Capital': 'Bruxelles',
    'Brussels': 'Bruxelles',
    'Bruxelles': 'Bruxelles',
    'Bruxelles-Capitale': 'Bruxelles',
    'Wallonia': 'Wallonie',
    'Wallonie': 'Wallonie',
    'Flanders': 'Flandre',
    'Flandre': 'Flandre',
    'Vlaanderen': 'Flandre'
  };
  
  // Check if region is Belgian
  const isBelgianRegion = (region) => {
    if (!region) return false;
    return Object.keys(BELGIAN_REGIONS).some(r => 
      region.toLowerCase().includes(r.toLowerCase()) || r.toLowerCase().includes(region.toLowerCase())
    );
  };
  
  // Normalize region name to standard Belgian region
  const normalizeRegion = (region) => {
    if (!region) return null;
    for (const [key, value] of Object.entries(BELGIAN_REGIONS)) {
      if (region.toLowerCase().includes(key.toLowerCase()) || key.toLowerCase().includes(region.toLowerCase())) {
        return value;
      }
    }
    return null; // Not a Belgian region
  };

  // Check if any filter is active
  const hasActiveFilters = () => {
    return filters.ageGroup !== 'all' || 
           filters.gender !== 'all' || 
           filters.region !== 'all' || 
           filters.domain !== 'all';
  };

  // Get filtered statistics summary
  const getFilteredStats = () => {
    if (!data) return null;
    
    // Get base counts from demographics
    const demographics = data.demographics || {};
    const ageDistribution = demographics.age_distribution || {};
    const genderDistribution = demographics.gender_distribution || {};
    
    // Map data for regional filtering
    const mapData = data.map_data || {};
    const byProvince = mapData.by_province || {};
    
    // Calculate filtered totals
    let filteredTotal = data.completion_rates?.total_sessions || 0;
    let filterDescription = [];
    
    // Apply age filter
    if (filters.ageGroup !== 'all') {
      const ageCount = ageDistribution[filters.ageGroup] || 0;
      filteredTotal = Math.min(filteredTotal, ageCount);
      filterDescription.push(`${filters.ageGroup} ans`);
    }
    
    // Apply gender filter
    if (filters.gender !== 'all') {
      const genderMap = { male: 'Hommes', female: 'Femmes', other: 'Autre' };
      const genderLabel = genderMap[filters.gender] || filters.gender;
      const genderCount = genderDistribution[genderLabel] || 0;
      filteredTotal = Math.min(filteredTotal, genderCount);
      filterDescription.push(genderLabel);
    }
    
    // Apply region filter
    if (filters.region !== 'all') {
      // Find matching region in data
      let regionCount = 0;
      for (const [region, count] of Object.entries(byProvince)) {
        const normalizedRegion = normalizeRegion(region);
        if (normalizedRegion === filters.region) {
          regionCount += count;
        }
      }
      filteredTotal = Math.min(filteredTotal, regionCount);
      filterDescription.push(filters.region);
    }
    
    // Apply domain filter
    if (filters.domain !== 'all') {
      const domainDistribution = data.domain_distribution || [];
      const domainData = domainDistribution.find(d => d.domain === filters.domain);
      if (domainData) {
        filteredTotal = Math.min(filteredTotal, domainData.count || 0);
      }
      filterDescription.push(`Domaine ${filters.domain}`);
    }
    
    return {
      total: filteredTotal,
      description: filterDescription.join(' • '),
      hasFilters: filterDescription.length > 0
    };
  };

  // Filter geographic data to only Belgian regions
  const getFilteredGeographicData = () => {
    if (!data?.map_data) return { by_province: {}, by_city: {} };
    
    const filteredByProvince = {};
    const filteredByCity = {};
    
    // Filter provinces/regions
    for (const [region, count] of Object.entries(data.map_data.by_province || {})) {
      const normalizedRegion = normalizeRegion(region);
      if (normalizedRegion) {
        filteredByProvince[normalizedRegion] = (filteredByProvince[normalizedRegion] || 0) + count;
      }
    }
    
    // Keep cities (they're already in Belgium if province is Belgian)
    for (const [city, count] of Object.entries(data.map_data.by_city || {})) {
      filteredByCity[city] = count;
    }
    
    return { by_province: filteredByProvince, by_city: filteredByCity };
  };

  // Get UI label with fallback
  // Local UI labels (fallback)
  const localLabels = {
    fr: {
      title: "Analytics Déclic",
      subtitle: "Tableau de bord d'analyse",
      tabs: {
        overview: "Vue d'ensemble",
        demographics: "Démographie",
        careers: "Métiers",
        cross: "Analyses croisées",
        geography: "Géographie",
        insights: "Insights"
      },
      export: {
        pdf: "export.pdf",
        csv: "export.csv"
      },
      charts: {
        completion_funnel: "Funnel de complétion",
        conversion_funnel: "Funnel de conversion",
        top_domains: "Domaines les plus populaires",
        domain_distribution: "Distribution par domaine",
        age_distribution: "Distribution par âge",
        gender_distribution: "Distribution par genre",
        most_suggested: "Métiers les plus suggérés",
        most_selected: "Métiers les plus sélectionnés",
        careers_by_age: "Métiers populaires par âge",
        careers_by_gender: "Métiers populaires par genre",
        key_indicators: "Indicateurs clés",
        total_sessions: "Sessions totales",
        declaration_rate: "Taux de déclaration",
        domains_represented: "Domaines représentés",
        suggested_careers: "Métiers suggérés",
        realtime_stats: "Statistiques en temps réel",
        active_now: "Actifs maintenant",
        today: "Aujourd'hui",
        this_week: "Cette semaine",
        this_month: "Ce mois",
        today_completion: "Complétion aujourd'hui",
        data_quality: "Qualité des données",
        data_quality_note: "Les données sont collectées de manière anonyme et agrégées pour l'analyse.",
        last_update: "Dernière mise à jour",
        impact: "Impact ressenti après le test",
        careers_by_segment: "Métiers populaires par segment (Genre × Âge)",
        progression_rates: "Taux de progression",
        usage_evolution: "Évolution d'utilisation",
        interactive_map: "Carte interactive de Belgique",
        top_cities: "Top villes",
        top_municipalities: "Top communes"
      },
      interpretations: {
        title: "Interprétations automatiques",
        subtitle: "Analyses générées automatiquement à partir des données"
      }
    },
    nl: {
      title: "Analytics Déclic",
      subtitle: "Analyse dashboard",
      tabs: {
        overview: "Overzicht",
        demographics: "Demografie",
        careers: "Beroepen",
        cross: "Kruisanalyse",
        geography: "Geografie",
        insights: "Inzichten"
      },
      export: {
        pdf: "export.pdf",
        csv: "export.csv"
      },
      charts: {
        completion_funnel: "Voltooiingsfunnel",
        conversion_funnel: "Conversiefunnel",
        top_domains: "Populairste domeinen",
        domain_distribution: "Verdeling per domein",
        age_distribution: "Verdeling per leeftijd",
        gender_distribution: "Verdeling per geslacht",
        most_suggested: "Meest gesuggereerde beroepen",
        most_selected: "Meest geselecteerde beroepen",
        careers_by_age: "Populaire beroepen per leeftijd",
        careers_by_gender: "Populaire beroepen per geslacht",
        key_indicators: "Belangrijke indicatoren",
        total_sessions: "Totaal sessies",
        declaration_rate: "Declaratiegraad",
        domains_represented: "Vertegenwoordigde domeinen",
        suggested_careers: "Gesuggereerde beroepen",
        realtime_stats: "Realtime statistieken",
        active_now: "Nu actief",
        today: "Vandaag",
        this_week: "Deze week",
        this_month: "Deze maand",
        today_completion: "Voltooiing vandaag",
        data_quality: "Gegevenskwaliteit",
        data_quality_note: "Gegevens worden anoniem verzameld en geaggregeerd voor analyse.",
        last_update: "Laatste update",
        impact: "Ervaren impact na de test",
        careers_by_segment: "Populaire beroepen per segment (Geslacht × Leeftijd)",
        progression_rates: "Voortgangspercentages",
        usage_evolution: "Gebruiksevolutie",
        interactive_map: "Interactieve kaart van België",
        top_cities: "Top steden",
        top_municipalities: "Top gemeenten"
      },
      interpretations: {
        title: "Automatische interpretaties",
        subtitle: "Automatisch gegenereerde analyses op basis van de gegevens"
      }
    }
  };

  const getLabel = (path) => {
    // First try from backend data
    if (data?.ui_labels) {
      const keys = path.split('.');
      let value = data.ui_labels;
      for (const key of keys) {
        value = value?.[key];
      }
      if (value) return value;
    }
    // Fallback to local labels
    const keys = path.split('.');
    let value = localLabels[dashboardLang] || localLabels.fr;
    for (const key of keys) {
      value = value?.[key];
    }
    return value || path;
  };

  const exportCSV = async () => {
    try {
      const response = await axios.get(`${API}/analytics/export`);
      const csvData = response.data.data;
      
      // Convert to CSV
      if (csvData.length === 0) return;
      
      // Add Déclic branding header
      const brandingHeader = [
        '# DÉCLIC BELGIUM - Analytics Export',
        `# Généré le / Gegenereerd op: ${new Date().toLocaleString('fr-BE')}`,
        `# Total records / Totaal records: ${csvData.length}`,
        `# Website: www.declicbelgium.be`,
        '#',
        ''
      ].join('\n');
      
      const headers = Object.keys(csvData[0]);
      const csv = brandingHeader + [
        headers.join(','),
        ...csvData.map(row => 
          headers.map(h => {
            const val = row[h];
            if (Array.isArray(val)) return `"${val.join(';')}"`;
            if (typeof val === 'object') return `"${JSON.stringify(val)}"`;
            return `"${val || ''}"`;
          }).join(',')
        )
      ].join('\n');
      
      // Use BOM for Excel UTF-8 compatibility
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `declic-analytics-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  const exportPDF = async () => {
    try {
      // Download PDF from backend with Déclic branding
      const response = await axios.get(`${API}/analytics/export-pdf?lang=${dashboardLang}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `declic-analytics-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF export error:', err);
      // Fallback to browser print
      window.print();
    }
  };

  const generateReportSummary = () => {
    if (!data) return null;
    
    const summary = {
      totalSessions: data.completion_rates?.total_sessions || 0,
      stage1Rate: data.completion_rates?.stage1_rate || 0,
      stage2Rate: data.completion_rates?.stage2_start_rate || 0,
      topDomain: data.domain_distribution?.[0]?.domain || 'N/A',
      topCareer: data.career_popularity?.most_suggested?.[0]?.career || 'N/A',
      topAge: Object.entries(data.demographics?.age_distribution || {})
        .filter(([k]) => k && k !== 'null')
        .sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A',
      topCity: Object.entries(data.map_data?.by_city || {})
        .filter(([k]) => k && k !== 'null')
        .sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A'
    };
    
    return summary;
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1D1D1D] to-[#A4187A] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          {/* Language Toggle */}
          <div className="flex justify-center gap-2 mb-6">
            <button
              onClick={() => handleLanguageChange('fr')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                dashboardLang === 'fr'
                  ? 'bg-[#A4187A] text-white'
                  : 'bg-[#F3E5F5] text-[#A4187A] hover:bg-[#E1BEE7]'
              }`}
            >
              🇫🇷 Français
            </button>
            <button
              onClick={() => handleLanguageChange('nl')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                dashboardLang === 'nl'
                  ? 'bg-[#FB6823] text-white'
                  : 'bg-[#FFF3E0] text-[#FB6823] hover:bg-[#FFE0B2]'
              }`}
            >
              🇳🇱 Nederlands
            </button>
          </div>

          <div className="text-center mb-6">
            <Lock className="w-12 h-12 text-[#A4187A] mx-auto mb-3" />
            <h1 className="text-2xl font-bold text-[#1D1D1D]">
              {dashboardLang === 'fr' ? 'Analyse des données Déclic' : 'Déclic gegevensanalyse'}
            </h1>
            <p className="text-[#1D1D1D]/60 text-sm mt-2">
              {dashboardLang === 'fr' ? 'Accès réservé aux administrateurs' : 'Toegang alleen voor beheerders'}
            </p>
          </div>
          
          <form onSubmit={handleLogin}>
            <div className="relative mb-4">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={dashboardLang === 'fr' ? 'Mot de passe administrateur' : 'Beheerderswachtwoord'}
                className="w-full px-4 py-3 pr-12 rounded-xl border-2 border-[#E0E0E0] focus:border-[#A4187A] focus:outline-none"
                data-testid="admin-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1D1D1D]/40"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            
            {authError && (
              <p className="text-red-500 text-sm mb-4">{authError}</p>
            )}
            
            <button
              type="submit"
              className="w-full py-3 bg-gradient-to-r from-[#A4187A] to-[#8E24AA] text-white rounded-xl font-bold hover:shadow-lg transition-all"
              data-testid="admin-login-btn"
            >
              {dashboardLang === 'fr' ? 'Accéder au tableau de bord' : 'Ga naar het dashboard'}
            </button>
          </form>
          
          <button
            onClick={() => navigate('/admin')}
            className="w-full mt-4 py-2 text-[#A4187A] text-sm hover:underline flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {dashboardLang === 'fr' ? 'Retour au dashboard simple' : 'Terug naar eenvoudig dashboard'}
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#EBEBEB] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-[#A4187A] animate-spin mx-auto mb-4" />
          <p className="text-[#1D1D1D]/60">Chargement des analyses...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#EBEBEB] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button onClick={fetchData} className="px-4 py-2 bg-[#A4187A] text-white rounded-lg">
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // Main dashboard
  return (
    <div className="min-h-screen bg-[#EBEBEB]" ref={chartsRef}>
      {/* Header */}
      <header className="bg-gradient-to-r from-[#A4187A] to-[#8E24AA] text-white py-6 px-4 no-print">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              {/* Back to Admin Button */}
              <button
                onClick={() => navigate('/admin')}
                className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors flex items-center gap-2"
                title={dashboardLang === 'fr' ? 'Retour à Admin' : 'Terug naar Admin'}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold">{getLabel('title')}</h1>
                <p className="text-white/70 text-sm">{getLabel('subtitle')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Language Toggle */}
              <div className="flex bg-white/20 rounded-lg p-1 mr-2">
                <button
                  onClick={() => handleLanguageChange('fr')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    dashboardLang === 'fr'
                      ? 'bg-white text-[#A4187A]'
                      : 'text-white/80 hover:text-white'
                  }`}
                  title="Français"
                >
                  FR
                </button>
                <button
                  onClick={() => handleLanguageChange('nl')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    dashboardLang === 'nl'
                      ? 'bg-white text-[#FB6823]'
                      : 'text-white/80 hover:text-white'
                  }`}
                  title="Nederlands"
                >
                  NL
                </button>
              </div>
              
              {/* PART 2: Manual refresh button with timestamp */}
              <div className="flex items-center gap-2">
                <button
                  onClick={handleManualRefresh}
                  disabled={isRefreshing}
                  className={`px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors flex items-center gap-2 ${
                    isRefreshing ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  title={dashboardLang === 'fr' ? 'Actualiser les données' : 'Gegevens vernieuwen'}
                  data-testid="refresh-data-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline text-sm">
                    {dashboardLang === 'fr' ? 'Actualiser' : 'Vernieuwen'}
                  </span>
                </button>
                {lastUpdated && (
                  <span className="text-xs text-white/70 bg-white/10 px-2 py-1 rounded">
                    {dashboardLang === 'fr' ? 'Màj: ' : 'Update: '}
                    {lastUpdated.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </span>
                )}
              </div>
              
              {/* Observatory Link */}
              <button
                onClick={() => navigate('/observatory')}
                className="px-4 py-2 bg-gradient-to-r from-[#FB6823] to-[#FFC107] rounded-lg hover:shadow-lg transition-all flex items-center gap-2"
                title={dashboardLang === 'fr' ? 'Observatoire National' : 'Nationaal Observatorium'}
              >
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline">{dashboardLang === 'fr' ? 'Observatoire' : 'Observatorium'}</span>
              </button>
              
              {/* Plaidoyer Link */}
              <button
                onClick={() => navigate('/plaidoyer')}
                className="px-4 py-2 bg-gradient-to-r from-[#FB6823] to-[#FFC107] rounded-lg hover:shadow-lg transition-all flex items-center gap-2"
                title={dashboardLang === 'fr' ? 'Plaidoyer Déclic' : 'Déclic Pleidooi'}
                data-testid="analytics-plaidoyer-btn"
              >
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">{dashboardLang === 'fr' ? 'Plaidoyer' : 'Pleidooi'}</span>
              </button>
              
              {/* PART 10: Manual Refresh Button with Timestamp */}
              <div className="flex items-center gap-2">
                {lastUpdated && (
                  <span className="text-xs text-white/70 hidden md:inline">
                    {dashboardLang === 'fr' ? 'Màj:' : 'Upd:'} {lastUpdated.toLocaleTimeString()}
                  </span>
                )}
                <button
                  onClick={handleManualRefresh}
                  disabled={isRefreshing}
                  className={`px-3 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors flex items-center gap-2 ${isRefreshing ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={dashboardLang === 'fr' ? 'Actualiser les données' : 'Gegevens vernieuwen'}
                >
                  <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline text-sm">
                    {dashboardLang === 'fr' ? 'Actualiser' : 'Vernieuwen'}
                  </span>
                </button>
              </div>
              <button
                onClick={exportPDF}
                className="px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors flex items-center gap-2"
                title="Export PDF"
              >
                <FileDown className="w-4 h-4" />
                <span className="hidden sm:inline">{getLabel('export.pdf')}</span>
              </button>
              <button
                onClick={exportCSV}
                className="px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">{getLabel('export.csv')}</span>
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {[
              { id: 'overview', label: getLabel('tabs.overview'), icon: BarChart3 },
              { id: 'demographics', label: getLabel('tabs.demographics'), icon: Users },
              { id: 'careers', label: getLabel('tabs.careers'), icon: Target },
              { id: 'impact', label: dashboardLang === 'fr' ? 'Impact éducatif' : 'Educatieve impact', icon: TrendingUp },
              { id: 'cross', label: getLabel('tabs.cross'), icon: PieChart },
              { id: 'geography', label: getLabel('tabs.geography'), icon: MapPin },
              { id: 'results', label: dashboardLang === 'fr' ? 'Résultats utilisateurs' : 'Gebruikersresultaten', icon: FileText },
              { id: 'insights', label: getLabel('tabs.insights'), icon: Lightbulb }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-white text-[#A4187A] font-medium'
                    : 'bg-white/20 hover:bg-white/30'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto p-4 space-y-6">
        
        {/* PART 7: Filters Panel */}
        <section className="bg-white rounded-xl p-4 border border-[#E0E0E0] no-print">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-[#1D1D1D] flex items-center gap-2">
              <Filter className="w-5 h-5 text-[#A4187A]" />
              {dashboardLang === 'fr' ? 'Filtres' : 'Filters'}
            </h3>
            <button
              onClick={resetFilters}
              className="text-sm text-[#A4187A] hover:underline"
              data-testid="reset-filters-btn"
            >
              {dashboardLang === 'fr' ? 'Réinitialiser' : 'Reset'}
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Age Group Filter */}
            <div>
              <label className="text-xs text-[#1D1D1D]/60 mb-1 block">
                {dashboardLang === 'fr' ? 'Groupe d\'âge' : 'Leeftijdsgroep'}
              </label>
              <select
                value={filters.ageGroup}
                onChange={(e) => handleFilterChange('ageGroup', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:border-[#A4187A] focus:ring-1 focus:ring-[#A4187A]"
                data-testid="filter-age"
              >
                <option value="all">{dashboardLang === 'fr' ? 'Tous les âges' : 'Alle leeftijden'}</option>
                <option value="13-15">13-15 ans</option>
                <option value="16-18">16-18 ans</option>
                <option value="19-22">19-22 ans</option>
                <option value="23+">23+ ans</option>
              </select>
            </div>
            
            {/* Gender Filter */}
            <div>
              <label className="text-xs text-[#1D1D1D]/60 mb-1 block">
                {dashboardLang === 'fr' ? 'Genre' : 'Geslacht'}
              </label>
              <select
                value={filters.gender}
                onChange={(e) => handleFilterChange('gender', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:border-[#A4187A] focus:ring-1 focus:ring-[#A4187A]"
                data-testid="filter-gender"
              >
                <option value="all">{dashboardLang === 'fr' ? 'Tous les genres' : 'Alle geslachten'}</option>
                <option value="male">{dashboardLang === 'fr' ? 'Hommes' : 'Mannen'}</option>
                <option value="female">{dashboardLang === 'fr' ? 'Femmes' : 'Vrouwen'}</option>
                <option value="other">{dashboardLang === 'fr' ? 'Autre' : 'Anders'}</option>
              </select>
            </div>
            
            {/* Region Filter - Only Belgian regions */}
            <div>
              <label className="text-xs text-[#1D1D1D]/60 mb-1 block">
                {dashboardLang === 'fr' ? 'Région (Belgique)' : 'Regio (België)'}
              </label>
              <select
                value={filters.region}
                onChange={(e) => handleFilterChange('region', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:border-[#A4187A] focus:ring-1 focus:ring-[#A4187A]"
                data-testid="filter-region"
              >
                <option value="all">{dashboardLang === 'fr' ? 'Toute la Belgique' : 'Heel België'}</option>
                <option value="Bruxelles">🏙️ Bruxelles-Capitale</option>
                <option value="Wallonie">🌲 Wallonie</option>
                <option value="Flandre">🌊 Flandre / Vlaanderen</option>
              </select>
            </div>
            
            {/* Domain Filter */}
            <div>
              <label className="text-xs text-[#1D1D1D]/60 mb-1 block">
                {dashboardLang === 'fr' ? 'Domaine professionnel' : 'Beroepsdomein'}
              </label>
              <select
                value={filters.domain}
                onChange={(e) => handleFilterChange('domain', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:border-[#A4187A] focus:ring-1 focus:ring-[#A4187A]"
                data-testid="filter-domain"
              >
                <option value="all">{dashboardLang === 'fr' ? 'Tous les domaines' : 'Alle domeinen'}</option>
                {Object.keys(DOMAIN_COLORS).map(domain => (
                  <option key={domain} value={domain}>{domain}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Filtered Results Summary - Only show when filters are active */}
          {hasActiveFilters() && (
            <div className="mt-4 p-4 bg-gradient-to-r from-[#A4187A]/10 to-[#FB6823]/10 rounded-lg border border-[#A4187A]/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#A4187A] rounded-full flex items-center justify-center">
                    <span className="text-white text-lg font-bold">
                      {getFilteredStats()?.total || 0}
                    </span>
                  </div>
                  <div>
                    <div className="font-semibold text-[#1D1D1D]">
                      {dashboardLang === 'fr' ? 'Résultats correspondants' : 'Overeenkomende resultaten'}
                    </div>
                    <div className="text-sm text-[#1D1D1D]/70">
                      {getFilteredStats()?.description || ''}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-[#1D1D1D]/50">
                    {dashboardLang === 'fr' ? 'sur' : 'van'} {data?.completion_rates?.total_sessions || 0} {dashboardLang === 'fr' ? 'total' : 'totaal'}
                  </div>
                  <div className="text-lg font-bold text-[#A4187A]">
                    {data?.completion_rates?.total_sessions 
                      ? Math.round((getFilteredStats()?.total || 0) / data.completion_rates.total_sessions * 100)
                      : 0}%
                  </div>
                </div>
              </div>
              
              {/* Active filters tags */}
              <div className="mt-3 flex flex-wrap gap-2">
                {filters.ageGroup !== 'all' && (
                  <span className="px-2 py-1 bg-white rounded-full text-xs font-medium text-[#A4187A] border border-[#A4187A]/30 flex items-center gap-1">
                    👤 {filters.ageGroup} ans
                    <button onClick={() => handleFilterChange('ageGroup', 'all')} className="ml-1 hover:text-red-500">×</button>
                  </span>
                )}
                {filters.gender !== 'all' && (
                  <span className="px-2 py-1 bg-white rounded-full text-xs font-medium text-[#A4187A] border border-[#A4187A]/30 flex items-center gap-1">
                    {filters.gender === 'male' ? '♂️' : filters.gender === 'female' ? '♀️' : '⚧️'} {filters.gender === 'male' ? 'Hommes' : filters.gender === 'female' ? 'Femmes' : 'Autre'}
                    <button onClick={() => handleFilterChange('gender', 'all')} className="ml-1 hover:text-red-500">×</button>
                  </span>
                )}
                {filters.region !== 'all' && (
                  <span className="px-2 py-1 bg-white rounded-full text-xs font-medium text-[#A4187A] border border-[#A4187A]/30 flex items-center gap-1">
                    📍 {filters.region}
                    <button onClick={() => handleFilterChange('region', 'all')} className="ml-1 hover:text-red-500">×</button>
                  </span>
                )}
                {filters.domain !== 'all' && (
                  <span className="px-2 py-1 bg-white rounded-full text-xs font-medium text-[#A4187A] border border-[#A4187A]/30 flex items-center gap-1">
                    🎯 {filters.domain}
                    <button onClick={() => handleFilterChange('domain', 'all')} className="ml-1 hover:text-red-500">×</button>
                  </span>
                )}
              </div>
            </div>
          )}
        </section>

        {/* Overview Tab */}
        {activeTab === 'overview' && data && (
          <>
            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard
                icon={Users}
                label="Tests démarrés"
                value={data.completion_rates?.total_sessions || 0}
                color="#A4187A"
                subtitle="Sessions créées"
              />
              <MetricCard
                icon={CheckCircle}
                label="Étape 1 terminée"
                value={`${data.completion_rates?.stage1_completed || 0}/${data.completion_rates?.total_sessions || 0} (${data.completion_rates?.stage1_rate || 0}%)`}
                color="#4CAF50"
                subtitle="Ont complété le questionnaire"
              />
              <MetricCard
                icon={TrendingUp}
                label="Passage à l'Étape 2"
                value={`${data.completion_rates?.stage2_started || 0}/${data.completion_rates?.stage1_completed || 0} (${data.completion_rates?.stage2_start_rate || 0}%)`}
                color="#FB6823"
                subtitle="Ont continué vers l'Étape 2"
              />
              <MetricCard
                icon={Target}
                label="Parcours complet"
                value={`${data.completion_rates?.stage2_completed || 0}/${data.completion_rates?.total_sessions || 0} (${data.completion_rates?.overall_completion_rate || 0}%)`}
                color="#2196F3"
                subtitle="Ont terminé les 2 étapes"
              />
            </div>

            {/* Real-time stats banner */}
            {data.realtime_stats && (
              <section className="bg-gradient-to-r from-[#A4187A]/10 to-[#FB6823]/10 rounded-xl p-6 border border-[#A4187A]/20">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-[#1D1D1D] flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                    {getLabel('charts.realtime_stats') || 'Statistiques en temps réel'}
                  </h2>
                  <span className="text-xs text-[#1D1D1D]/50">
                    {new Date(data.realtime_stats.timestamp).toLocaleTimeString()}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-white rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-[#A4187A]">{data.realtime_stats.active_now || 0}</div>
                    <div className="text-xs text-[#1D1D1D]/60">{getLabel('charts.active_now') || 'Actifs maintenant'}</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-[#FB6823]">{data.realtime_stats.today || 0}</div>
                    <div className="text-xs text-[#1D1D1D]/60">{getLabel('charts.today') || "Aujourd'hui"}</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-[#2196F3]">{data.realtime_stats.this_week || 0}</div>
                    <div className="text-xs text-[#1D1D1D]/60">{getLabel('charts.this_week') || 'Cette semaine'}</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-[#4CAF50]">{data.realtime_stats.this_month || 0}</div>
                    <div className="text-xs text-[#1D1D1D]/60">{getLabel('charts.this_month') || 'Ce mois'}</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-[#9C27B0]">{data.realtime_stats.today_completion_rate || 0}%</div>
                    <div className="text-xs text-[#1D1D1D]/60">{getLabel('charts.today_completion') || 'Complétion auj.'}</div>
                  </div>
                </div>
              </section>
            )}

            {/* Conversion Funnel */}
            {data.conversion_funnel && (
              <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
                <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                  <Filter className="w-5 h-5 text-[#A4187A]" />
                  {getLabel('charts.conversion_funnel') || 'Funnel de conversion'}
                </h2>
                <div className="space-y-3">
                  {data.conversion_funnel.funnel?.map((stage, idx) => (
                    <div key={idx} className="relative">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-[#1D1D1D]">{stage.stage}</span>
                        <span className="text-sm text-[#1D1D1D]/70">{stage.count} ({stage.percentage}%)</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-6 overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                          style={{ 
                            width: `${stage.percentage}%`,
                            backgroundColor: COLORS[idx % COLORS.length],
                            minWidth: stage.percentage > 0 ? '40px' : '0'
                          }}
                        >
                          {stage.percentage >= 10 && (
                            <span className="text-xs text-white font-medium">{stage.percentage}%</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Conversion rates summary */}
                <div className="mt-6 pt-4 border-t border-gray-100 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-lg font-bold text-[#A4187A]">{data.conversion_funnel.conversion_rates?.start_to_stage1 || 0}%</div>
                    <div className="text-xs text-[#1D1D1D]/50">Démarrage → Stage 1</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-[#FB6823]">{data.conversion_funnel.conversion_rates?.stage1_to_stage2 || 0}%</div>
                    <div className="text-xs text-[#1D1D1D]/50">Stage 1 → Stage 2</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-[#4CAF50]">{data.conversion_funnel.conversion_rates?.stage2_start_to_complete || 0}%</div>
                    <div className="text-xs text-[#1D1D1D]/50">Stage 2 complet</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-[#2196F3]">{data.conversion_funnel.conversion_rates?.overall || 0}%</div>
                    <div className="text-xs text-[#1D1D1D]/50">Conversion totale</div>
                  </div>
                </div>
              </section>
            )}

            {/* Usage over time */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#A4187A]" />
                {getLabel('charts.usage_evolution')}
              </h2>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data.usage_over_time || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="users" 
                    stroke="#A4187A" 
                    strokeWidth={2}
                    name="Utilisateurs"
                    dot={{ fill: '#A4187A' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </section>

            {/* Completion rates bar chart */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#FB6823]" />
                {getLabel('charts.progression_rates')}
              </h2>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={[
                  { name: 'Stage 1 complété', value: data.completion_rates?.stage1_rate || 0 },
                  { name: 'Stage 2 commencé', value: data.completion_rates?.stage2_start_rate || 0 },
                  { name: 'Stage 2 complété', value: data.completion_rates?.stage2_complete_rate || 0 }
                ]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} unit="%" />
                  <Tooltip formatter={(value) => `${value}%`} />
                  <Bar dataKey="value" fill="#A4187A" radius={[4, 4, 0, 0]}>
                    {[0, 1, 2].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </section>

            {/* Domain distribution pie chart */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
                <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-[#4CAF50]" />
                  {getLabel('charts.domain_distribution')}
                </h2>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPie>
                    <Pie
                      data={data.domain_distribution || []}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      dataKey="count"
                      nameKey="domain"
                      label={({ domain, percent }) => `${domain} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={false}
                    >
                      {(data.domain_distribution || []).map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={DOMAIN_COLORS[entry.domain] || COLORS[index % COLORS.length]} 
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RechartsPie>
                </ResponsiveContainer>
              </section>

              <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
                <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-[#FFC107]" />
                  {getLabel('charts.impact')}
                </h2>
                {/* Post-test impact - with properly formatted labels and aggregation */}
                {(() => {
                  // Map impact keys to proper French labels
                  const impactLabels = {
                    'discovered_new': 'Découverte de nouveaux métiers',
                    'confirmed_idea': 'Confirmation d\'une idée',
                    'new_directions': 'Nouvelles pistes à explorer',
                    'still_reflecting': 'Encore en réflexion'
                  };
                  
                  // Process and clean impact data - filter out empty/null keys
                  const rawData = (data.impact_distribution || [])
                    .filter(item => item.count > 0 && item.key && item.key !== '' && item.key !== 'null')
                    .map(item => {
                      // Use the 'impact' field if it's a proper label, otherwise translate the key
                      let label = item.impact;
                      if (item.key && impactLabels[item.key]) {
                        label = impactLabels[item.key];
                      } else if (label && label.includes(',')) {
                        // Handle combined keys like "discovered_new,still_reflecting"
                        label = impactLabels[label.split(',')[0]] || label;
                      } else if (label && impactLabels[label]) {
                        label = impactLabels[label];
                      }
                      return { ...item, impact: label };
                    });
                  
                  // AGGREGATE data with same label
                  const aggregatedMap = {};
                  rawData.forEach(item => {
                    const label = item.impact;
                    if (aggregatedMap[label]) {
                      aggregatedMap[label].count += item.count;
                    } else {
                      aggregatedMap[label] = { impact: label, count: item.count };
                    }
                  });
                  
                  // Convert back to array and sort by count descending
                  const cleanedImpactData = Object.values(aggregatedMap)
                    .sort((a, b) => b.count - a.count);
                  
                  const totalResponses = cleanedImpactData.reduce((sum, item) => sum + (item.count || 0), 0);
                  
                  // If no data, show message
                  if (cleanedImpactData.length === 0 || totalResponses === 0) {
                    return (
                      <div className="flex items-center justify-center h-[300px] text-[#1D1D1D]/50">
                        <div className="text-center">
                          <Lightbulb className="w-12 h-12 mx-auto mb-2 opacity-30" />
                          <p>{dashboardLang === 'fr' ? 'Pas encore de réponses au questionnaire post-test' : 'Nog geen antwoorden op de post-test vragenlijst'}</p>
                        </div>
                      </div>
                    );
                  }
                  
                  return (
                    <>
                      <p className="text-sm text-[#1D1D1D]/60 mb-4">
                        {dashboardLang === 'fr' 
                          ? `Basé sur ${totalResponses} répondant${totalResponses > 1 ? 's' : ''} ayant répondu à la question post-test`
                          : `Gebaseerd op ${totalResponses} respondent${totalResponses > 1 ? 'en' : ''} die de post-test vraag hebben beantwoord`}
                      </p>
                      <ResponsiveContainer width="100%" height={300}>
                        <RechartsPie>
                          <Pie
                            data={cleanedImpactData}
                            cx="40%"
                            cy="50%"
                            outerRadius={90}
                            dataKey="count"
                            nameKey="impact"
                            label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                          >
                            {cleanedImpactData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip 
                            formatter={(value, name) => [
                              `${value} (${totalResponses > 0 ? ((value / totalResponses) * 100).toFixed(1) : 0}%)`,
                              name
                            ]}
                          />
                          <Legend 
                            layout="vertical" 
                            align="right" 
                            verticalAlign="middle"
                            formatter={(value) => <span className="text-sm">{value}</span>}
                          />
                        </RechartsPie>
                      </ResponsiveContainer>
                    </>
                  );
                })()}
              </section>
            </div>
          </>
        )}

        {/* Demographics Tab */}
        {activeTab === 'demographics' && data && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Age distribution */}
              <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
                <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#A4187A]" />
                  {getLabel('charts.age_distribution')}
                </h2>
                {/* PART 4: Age display with percentage first */}
                {(() => {
                  const ageData = Object.entries(data.demographics?.age_distribution || {})
                    .filter(([k]) => k && k !== 'null')
                    .map(([age, count]) => ({ age, count }))
                    .sort((a, b) => a.age.localeCompare(b.age));
                  const totalUsers = ageData.reduce((sum, item) => sum + item.count, 0);
                  const ageDataWithPercent = ageData.map(item => ({
                    ...item,
                    percentage: totalUsers > 0 ? ((item.count / totalUsers) * 100).toFixed(1) : 0,
                    label: `${item.age} ans : ${totalUsers > 0 ? ((item.count / totalUsers) * 100).toFixed(0) : 0}% (${item.count})`
                  }));
                  return (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                        {ageDataWithPercent.map((item, idx) => (
                          <div key={idx} className="bg-[#F3E5F5] rounded-lg p-3 text-center">
                            <div className="text-xl font-bold text-[#A4187A]">{item.percentage}%</div>
                            <div className="text-xs text-[#1D1D1D]/70">{item.age} ans</div>
                            <div className="text-xs text-[#1D1D1D]/50">({item.count} utilisateurs)</div>
                          </div>
                        ))}
                      </div>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={ageDataWithPercent}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                          <XAxis dataKey="age" tick={{ fontSize: 12 }} tickFormatter={(v) => `${v} ans`} />
                          <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}%`} />
                          <Tooltip 
                            formatter={(value, name, props) => [`${props.payload.percentage}% (${props.payload.count})`, 'Utilisateurs']}
                            labelFormatter={(label) => `${label} ans`}
                          />
                          <Bar dataKey="percentage" fill="#A4187A" radius={[4, 4, 0, 0]} name="Pourcentage" />
                        </BarChart>
                      </ResponsiveContainer>
                    </>
                  );
                })()}
              </section>

              {/* Gender distribution */}
              <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
                <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#E91E63]" />
                  {getLabel('charts.gender_distribution')}
                </h2>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPie>
                    <Pie
                      data={Object.entries(data.demographics?.gender_distribution || {})
                        .filter(([k]) => k && k !== 'null')
                        .map(([gender, count]) => ({
                          gender: formatGender(gender),
                          count
                        }))
                      }
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      dataKey="count"
                      nameKey="gender"
                      label={({ gender, percent }) => `${gender} (${(percent * 100).toFixed(0)}%)`}
                    >
                      <Cell fill="#E91E63" />
                      <Cell fill="#2196F3" />
                      <Cell fill="#9C27B0" />
                      <Cell fill="#607D8B" />
                    </Pie>
                    <Tooltip />
                  </RechartsPie>
                </ResponsiveContainer>
              </section>
            </div>

            {/* Engagement stats */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-[#FB6823]" />
                {getLabel('charts.engagement')}
              </h2>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.engagement_stats?.careers_selected_distribution || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis dataKey="count" type="category" tick={{ fontSize: 12 }} width={100} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#FB6823" radius={[0, 4, 4, 0]} name="Sessions" />
                </BarChart>
              </ResponsiveContainer>
            </section>
          </>
        )}

        {/* Careers Tab */}
        {activeTab === 'careers' && data && (
          <>
            {/* Most suggested careers */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-[#A4187A]" />
                {getLabel('charts.most_suggested')}
              </h2>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart 
                  data={(data.career_popularity?.most_suggested || []).slice(0, 10)} 
                  layout="vertical"
                  margin={{ left: 150 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis 
                    dataKey="career" 
                    type="category" 
                    tick={{ fontSize: 11 }} 
                    width={140}
                  />
                  <Tooltip />
                  <Bar dataKey="count" fill="#A4187A" radius={[0, 4, 4, 0]} name="Fois suggéré" />
                </BarChart>
              </ResponsiveContainer>
            </section>

            {/* Most selected careers */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#4CAF50]" />
                {getLabel('charts.most_selected')} (Stage 2)
              </h2>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart 
                  data={(data.career_popularity?.most_selected || []).slice(0, 10)} 
                  layout="vertical"
                  margin={{ left: 150 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis 
                    dataKey="career" 
                    type="category" 
                    tick={{ fontSize: 11 }} 
                    width={140}
                  />
                  <Tooltip />
                  <Bar dataKey="count" fill="#4CAF50" radius={[0, 4, 4, 0]} name="Fois sélectionné" />
                </BarChart>
              </ResponsiveContainer>
            </section>

            {/* PART 3: Sortable Careers Table */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <Table className="w-5 h-5 text-[#A4187A]" />
                {dashboardLang === 'fr' ? 'Tableau des métiers (triable)' : 'Beroepenlijst (sorteerbaar)'}
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#A4187A]/10">
                      <th 
                        className="p-3 text-left font-semibold cursor-pointer hover:bg-[#A4187A]/20"
                        onClick={() => handleSort('careers', 'career')}
                      >
                        {dashboardLang === 'fr' ? 'Métier' : 'Beroep'}
                        <SortIcon table="careers" column="career" />
                      </th>
                      <th 
                        className="p-3 text-right font-semibold cursor-pointer hover:bg-[#A4187A]/20"
                        onClick={() => handleSort('careers', 'count')}
                      >
                        {dashboardLang === 'fr' ? 'Suggestions' : 'Suggesties'}
                        <SortIcon table="careers" column="count" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedData(data.career_popularity?.most_suggested || [], 'careers').slice(0, 15).map((career, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="p-3 border-b border-gray-100">{career.career}</td>
                        <td className="p-3 border-b border-gray-100 text-right font-medium">{career.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}

        {/* PART 4: Educational Impact Tab */}
        {activeTab === 'impact' && (
          <>
            {/* Section Header */}
            <div className="bg-gradient-to-r from-[#A4187A]/10 to-[#FB6823]/10 rounded-xl p-6 border border-[#A4187A]/20">
              <h2 className="text-xl font-bold text-[#1D1D1D] mb-2 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-[#A4187A]" />
                {dashboardLang === 'fr' ? 'Impact éducatif du test' : 'Educatieve impact van de test'}
              </h2>
              <p className="text-[#1D1D1D]/70">
                {dashboardLang === 'fr' 
                  ? 'Indicateurs mesurant l\'effet du test sur la clarification des idées de carrière, l\'engagement et la progression des utilisateurs.'
                  : 'Indicatoren die het effect van de test meten op de verduidelijking van carrière-ideeën, betrokkenheid en voortgang van gebruikers.'
                }
              </p>
            </div>

            {educationalImpact ? (
              <>
                {/* 1. CLARIFICATION DES IDÉES */}
                <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
                  <h3 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-[#FB6823]" />
                    {educationalImpact.clarification?.title || 'Clarification des idées'}
                  </h3>
                  
                  {/* Key Metrics Cards */}
                  <div className="text-xs text-[#1D1D1D]/60 mb-2 flex items-center gap-1">
                    <span className="inline-block w-2 h-2 bg-[#A4187A] rounded-full"></span>
                    {dashboardLang === 'fr' 
                      ? `Pourcentages calculés par rapport aux ${educationalImpact.clarification?.metrics?.total_respondents || 0} répondants ayant répondu aux questions avant/après`
                      : `Percentages berekend op basis van ${educationalImpact.clarification?.metrics?.total_respondents || 0} respondenten die de voor/na-vragen hebben beantwoord`}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 text-center border border-green-200">
                      <div className="text-3xl font-bold text-green-600">
                        {educationalImpact.clarification?.metrics?.discovery_rate || 0}%
                      </div>
                      <div className="text-xs text-green-700 mt-1">
                        {dashboardLang === 'fr' ? 'Taux de découverte' : 'Ontdekkingsgraad'}
                      </div>
                      <div className="text-xs text-green-600/70 mt-1">
                        {dashboardLang === 'fr' 
                          ? 'Sans idée → Nouveaux métiers' 
                          : 'Geen idee → Nieuwe beroepen'}
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 text-center border border-blue-200">
                      <div className="text-3xl font-bold text-blue-600">
                        {educationalImpact.clarification?.metrics?.confirmation_rate || 0}%
                      </div>
                      <div className="text-xs text-blue-700 mt-1">
                        {dashboardLang === 'fr' ? 'Taux de confirmation' : 'Bevestigingsgraad'}
                      </div>
                      <div className="text-xs text-blue-600/70 mt-1">
                        {dashboardLang === 'fr' 
                          ? 'Idée claire → Confirmée' 
                          : 'Duidelijk idee → Bevestigd'}
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 text-center border border-purple-200">
                      <div className="text-3xl font-bold text-purple-600">
                        {educationalImpact.clarification?.metrics?.new_directions_rate || 0}%
                      </div>
                      <div className="text-xs text-purple-700 mt-1">
                        {dashboardLang === 'fr' ? 'Nouvelles pistes' : 'Nieuwe richtingen'}
                      </div>
                      <div className="text-xs text-purple-600/70 mt-1">
                        {dashboardLang === 'fr' 
                          ? '% des répondants' 
                          : '% van de respondenten'}
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 text-center border border-orange-200">
                      <div className="text-3xl font-bold text-orange-600">
                        {educationalImpact.clarification?.metrics?.total_respondents || 0}
                      </div>
                      <div className="text-xs text-orange-700 mt-1">
                        {dashboardLang === 'fr' ? 'Total répondants' : 'Totaal respondenten'}
                      </div>
                      <div className="text-xs text-orange-600/70 mt-1">
                        {dashboardLang === 'fr' 
                          ? 'Ayant répondu aux 2 questions' 
                          : 'Die beide vragen hebben beantwoord'}
                      </div>
                    </div>
                  </div>

                  {/* Transition Matrix */}
                  <div className="overflow-x-auto">
                    <h4 className="font-semibold text-[#1D1D1D] mb-3 flex items-center gap-2">
                      <Table className="w-4 h-4" />
                      {dashboardLang === 'fr' ? 'Matrice de transition Avant/Après' : 'Transitiematrix Voor/Na'}
                    </h4>
                    {educationalImpact.clarification?.matrix?.length > 0 ? (
                      (() => {
                        // Aggregate combined post_test responses and convert to %
                        const postLabels = {
                          'discovered_new': 'Découverte nouveaux métiers',
                          'confirmed_idea': 'Idée confirmée',
                          'new_directions': 'Nouvelles pistes',
                          'still_reflecting': 'Encore en réflexion'
                        };
                        
                        // Aggregate the data
                        const aggregatedMap = {};
                        let totalCount = 0;
                        
                        educationalImpact.clarification.matrix.forEach(row => {
                          // Handle combined post_test keys (e.g., "confirmed_idea,discovered_new")
                          const postKeys = row.post_test_key?.includes(',') 
                            ? row.post_test_key.split(',').map(k => k.trim())
                            : [row.post_test_key];
                          
                          postKeys.forEach(postKey => {
                            const key = `${row.pre_test}|${postKey}`;
                            if (!aggregatedMap[key]) {
                              aggregatedMap[key] = {
                                pre_test: row.pre_test,
                                post_test_key: postKey,
                                post_test: postLabels[postKey] || postKey,
                                count: 0
                              };
                            }
                            aggregatedMap[key].count += row.count;
                          });
                          totalCount += row.count;
                        });
                        
                        // Convert to array and sort
                        const aggregatedData = Object.values(aggregatedMap).sort((a, b) => b.count - a.count);
                        
                        return (
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-[#A4187A]/10">
                                <th 
                                  className="p-3 text-left font-semibold text-[#A4187A] cursor-pointer hover:bg-[#A4187A]/20"
                                  onClick={() => handleSort('crossAnalysis', 'pre_test')}
                                >
                                  {dashboardLang === 'fr' ? 'Avant le test' : 'Voor de test'}
                                  <SortIcon table="crossAnalysis" column="pre_test" />
                                </th>
                                <th 
                                  className="p-3 text-left font-semibold text-[#FB6823] cursor-pointer hover:bg-[#FB6823]/20"
                                  onClick={() => handleSort('crossAnalysis', 'post_test')}
                                >
                                  {dashboardLang === 'fr' ? 'Après le test' : 'Na de test'}
                                  <SortIcon table="crossAnalysis" column="post_test" />
                                </th>
                                <th 
                                  className="p-3 text-right font-semibold cursor-pointer hover:bg-gray-200"
                                  onClick={() => handleSort('crossAnalysis', 'count')}
                                >
                                  %
                                  <SortIcon table="crossAnalysis" column="count" />
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {aggregatedData.map((row, idx) => {
                                const percentage = totalCount > 0 ? ((row.count / totalCount) * 100).toFixed(1) : '0.0';
                                return (
                                  <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                                    <td className="p-3 border-b border-gray-100">{row.pre_test}</td>
                                    <td className="p-3 border-b border-gray-100">
                                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                        row.post_test_key === 'discovered_new' ? 'bg-green-100 text-green-700' :
                                        row.post_test_key === 'confirmed_idea' ? 'bg-blue-100 text-blue-700' :
                                        row.post_test_key === 'new_directions' ? 'bg-purple-100 text-purple-700' :
                                        'bg-orange-100 text-orange-700'
                                      }`}>
                                        {row.post_test}
                                      </span>
                                    </td>
                                    <td className="p-3 border-b border-gray-100 text-right font-medium">{percentage}%</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        );
                      })()
                    ) : (
                      <p className="text-center text-[#1D1D1D]/50 py-8">
                        {dashboardLang === 'fr' ? 'Données insuffisantes' : 'Onvoldoende gegevens'}
                      </p>
                    )}
                  </div>
                </section>

                {/* 2. ENGAGEMENT APRÈS LE TEST */}
                <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
                  <h3 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-[#A4187A]" />
                    {educationalImpact.engagement?.title || 'Engagement après le test'}
                  </h3>
                  
                  {/* Engagement Score */}
                  <div className="mb-6 bg-gradient-to-r from-[#A4187A] to-[#FB6823] rounded-xl p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm opacity-80">
                          {dashboardLang === 'fr' ? 'Score d\'engagement' : 'Betrokkenheidsscore'}
                        </div>
                        <div className="text-4xl font-bold">{educationalImpact.engagement?.score || 0}%</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm opacity-80">
                          {dashboardLang === 'fr' ? 'Total réponses' : 'Totaal antwoorden'}
                        </div>
                        <div className="text-2xl font-bold">{educationalImpact.engagement?.total || 0}</div>
                      </div>
                    </div>
                    <div className="mt-4 h-3 bg-white/30 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-white rounded-full transition-all duration-500"
                        style={{ width: `${educationalImpact.engagement?.score || 0}%` }}
                      />
                    </div>
                  </div>

                  {/* Engagement Distribution - Aggregated */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(() => {
                      // Labels for engagement intents
                      const engagementLabels = {
                        'meet_professional': dashboardLang === 'fr' ? 'Rencontrer un pro' : 'Ontmoet een professional',
                        'explore_training': dashboardLang === 'fr' ? 'Explorer formations' : 'Opleidingen verkennen',
                        'discuss_advisor': dashboardLang === 'fr' ? 'Discuter conseiller' : 'Adviseur raadplegen',
                        'continue_exploring': dashboardLang === 'fr' ? 'Continuer exploration' : 'Verder verkennen'
                      };
                      
                      // Aggregate combined intents
                      const aggregatedMap = {};
                      let totalResponses = 0;
                      
                      (educationalImpact.engagement?.data || []).forEach(item => {
                        const rawKey = item.key || item.label;
                        // Handle combined intents (e.g., "meet_professional,explore_training")
                        const intents = rawKey?.includes(',') 
                          ? rawKey.split(',').map(i => i.trim())
                          : [rawKey];
                        
                        intents.forEach(key => {
                          const label = engagementLabels[key] || key;
                          if (!aggregatedMap[label]) {
                            aggregatedMap[label] = { key, count: 0 };
                          }
                          aggregatedMap[label].count += item.count;
                        });
                        totalResponses += item.count;
                      });
                      
                      // Convert to array and calculate percentages
                      const aggregatedData = Object.entries(aggregatedMap)
                        .map(([label, data]) => ({
                          label,
                          key: data.key,
                          count: data.count,
                          percentage: totalResponses > 0 ? ((data.count / totalResponses) * 100).toFixed(1) : '0.0'
                        }))
                        .sort((a, b) => b.count - a.count);
                      
                      return aggregatedData.map((item, idx) => (
                        <div 
                          key={idx} 
                          className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <div 
                            className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm"
                            style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                          >
                            {item.percentage}%
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-[#1D1D1D]">{item.label}</div>
                            <div className="text-sm text-[#1D1D1D]/60">{item.count} {dashboardLang === 'fr' ? 'réponses' : 'antwoorden'}</div>
                          </div>
                          <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full"
                              style={{ 
                                width: `${item.percentage}%`,
                                backgroundColor: COLORS[idx % COLORS.length]
                              }}
                            />
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </section>

                {/* 3. PROGRESSION DANS L'OUTIL */}
                <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
                  <h3 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#4CAF50]" />
                    {educationalImpact.progression?.title || 'Progression dans l\'outil'}
                  </h3>
                  
                  {/* Progression Funnel */}
                  <div className="space-y-4">
                    {(() => {
                      const totalSessions = educationalImpact.progression?.data?.total_sessions || 0;
                      const stage1Rate = educationalImpact.progression?.data?.stage1_completion_rate || 0;
                      const stage2StartRate = educationalImpact.progression?.data?.stage2_start_rate || 0;
                      const overallRate = educationalImpact.progression?.data?.overall_completion_rate || 0;
                      
                      // Use raw counts if available, otherwise calculate
                      const stage1Count = Math.round(totalSessions * stage1Rate / 100);
                      const stage2StartedCount = educationalImpact.progression?.data?.stage2_started_count || 
                        Math.round(stage1Count * stage2StartRate / 100);
                      const stage2CompletedCount = educationalImpact.progression?.data?.stage2_completed_count ||
                        Math.round(totalSessions * overallRate / 100);
                      
                      return [
                        { 
                          label: dashboardLang === 'fr' ? 'Sessions créées' : 'Sessies aangemaakt',
                          value: totalSessions,
                          rate: 100,
                          color: '#A4187A'
                        },
                        { 
                          label: dashboardLang === 'fr' ? 'Étape 1 complétée' : 'Stap 1 voltooid',
                          value: stage1Count,
                          rate: stage1Rate,
                          color: '#FB6823'
                        },
                        { 
                          label: dashboardLang === 'fr' ? 'Étape 2 démarrée' : 'Stap 2 gestart',
                          value: stage2StartedCount,
                          rate: Math.min(stage2StartRate, 100), // Cap at 100%
                          color: '#2196F3'
                        },
                        { 
                          label: dashboardLang === 'fr' ? 'Parcours complet' : 'Volledig traject',
                          value: stage2CompletedCount,
                          rate: overallRate,
                          color: '#4CAF50'
                        }
                      ].map((stage, idx) => (
                        <div key={idx} className="relative">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-[#1D1D1D]">{stage.label}</span>
                            <span className="text-sm text-[#1D1D1D]/70">
                              {stage.value} ({stage.rate.toFixed(1)}%)
                            </span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-8 overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all duration-700 flex items-center justify-end pr-3"
                              style={{ 
                                width: `${Math.max(Math.min(stage.rate, 100), 5)}%`,
                                backgroundColor: stage.color
                              }}
                            >
                              <span className="text-white text-sm font-medium">{stage.rate.toFixed(1)}%</span>
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>

                  {/* Additional Metrics */}
                  <div className="mt-6 grid grid-cols-2 gap-4">
                    <div className="bg-[#4CAF50]/10 rounded-lg p-4 border border-[#4CAF50]/30">
                      <div className="text-2xl font-bold text-[#4CAF50]">
                        {educationalImpact.progression?.data?.avg_careers_selected?.toFixed(1) || 0}
                      </div>
                      <div className="text-sm text-[#4CAF50]/80">
                        {dashboardLang === 'fr' ? 'Carrières sélectionnées en moyenne' : 'Gemiddeld geselecteerde carrières'}
                      </div>
                    </div>
                    <div className="bg-[#2196F3]/10 rounded-lg p-4 border border-[#2196F3]/30">
                      <div className="text-2xl font-bold text-[#2196F3]">
                        {Math.min(educationalImpact.progression?.data?.stage2_completion_rate || 0, 100).toFixed(1)}%
                      </div>
                      <div className="text-sm text-[#2196F3]/80">
                        {dashboardLang === 'fr' ? 'Taux de complétion Étape 2' : 'Voltooiingspercentage Stap 2'}
                      </div>
                    </div>
                  </div>
                </section>

                {/* Interpretations */}
                {educationalImpact.interpretations?.length > 0 && (
                  <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
                    <h3 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-[#FFC107]" />
                      {dashboardLang === 'fr' ? 'Interprétations' : 'Interpretaties'}
                    </h3>
                    <div className="space-y-3">
                      {educationalImpact.interpretations.map((interp, idx) => (
                        <div 
                          key={idx} 
                          className={`p-4 rounded-lg border-l-4 ${
                            interp.type === 'positive' 
                              ? 'bg-green-50 border-green-500' 
                              : interp.type === 'warning'
                              ? 'bg-orange-50 border-orange-500'
                              : 'bg-blue-50 border-blue-500'
                          }`}
                        >
                          <div className={`font-semibold ${
                            interp.type === 'positive' ? 'text-green-700' :
                            interp.type === 'warning' ? 'text-orange-700' : 'text-blue-700'
                          }`}>
                            {interp.title}
                          </div>
                          <div className="text-sm text-[#1D1D1D]/70 mt-1">{interp.message}</div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </>
            ) : (
              <div className="bg-white rounded-xl p-12 text-center">
                <RefreshCw className="w-12 h-12 text-[#A4187A]/30 mx-auto mb-4" />
                <p className="text-[#1D1D1D]/60">
                  {dashboardLang === 'fr' 
                    ? 'Chargement des données d\'impact éducatif...' 
                    : 'Laden van educatieve impactgegevens...'}
                </p>
              </div>
            )}
          </>
        )}

        {/* Cross-analysis Tab */}
        {activeTab === 'cross' && data && (
          <>
            {/* NEW: Métiers populaires par segment (Genre × Âge) */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#FB6823]" />
                {getLabel('charts.careers_by_segment')}
              </h2>
              
              {/* View selector: Global / By Region */}
              {data.careers_by_gender_age && (
                <>
                  {/* Global view - Top careers all segments */}
                  <div className="mb-6">
                    <h3 className="font-semibold text-[#1D1D1D] mb-3 flex items-center gap-2">
                      <Globe className="w-4 h-4 text-[#A4187A]" />
                      {dashboardLang === 'fr' ? 'Vue globale - Top métiers tous segments' : 'Globaal overzicht - Top beroepen alle segmenten'}
                    </h3>
                    <div className="bg-gradient-to-r from-[#A4187A]/10 to-[#FB6823]/10 rounded-lg p-4">
                      <div className="flex flex-wrap gap-2">
                        {(data.careers_by_gender_age.global || []).slice(0, 10).map((career, idx) => (
                          <span 
                            key={idx} 
                            className="px-3 py-1 bg-white rounded-full text-sm border border-[#A4187A]/30 flex items-center gap-2"
                          >
                            <span className="w-5 h-5 bg-[#A4187A] text-white rounded-full flex items-center justify-center text-xs font-bold">
                              {idx + 1}
                            </span>
                            {career.career}
                            <span className="text-[#1D1D1D]/50 text-xs">({career.count})</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Segment cards Grid */}
                  <div className="mb-6">
                    <h3 className="font-semibold text-[#1D1D1D] mb-3 flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-[#FB6823]" />
                      {dashboardLang === 'fr' ? 'Par segment (Genre × Âge)' : 'Per segment (Geslacht × Leeftijd)'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(data.careers_by_gender_age.segments || {})
                        .filter(([key]) => key && !key.includes('null') && !key.includes('undefined'))
                        .sort(([a], [b]) => {
                          // Sort: Femmes first, then Hommes, then Autre; within gender sort by age
                          const aGender = a.split('_')[0];
                          const bGender = b.split('_')[0];
                          const genderOrder = {'Femmes': 0, 'Hommes': 1, 'Autre': 2};
                          if (genderOrder[aGender] !== genderOrder[bGender]) {
                            return (genderOrder[aGender] || 3) - (genderOrder[bGender] || 3);
                          }
                          return a.localeCompare(b);
                        })
                        .map(([key, segmentData]) => {
                          const isWomen = segmentData.gender === 'Femmes';
                          const isMen = segmentData.gender === 'Hommes';
                          return (
                            <div 
                              key={key} 
                              className={`rounded-lg p-4 border-l-4 ${
                                isWomen ? 'bg-[#FCE4EC] border-[#E91E63]' : 
                                isMen ? 'bg-[#E3F2FD] border-[#2196F3]' : 
                                'bg-[#F3E5F5] border-[#9C27B0]'
                              }`}
                            >
                              <h4 className={`font-bold mb-2 flex items-center gap-2 ${
                                isWomen ? 'text-[#E91E63]' : 
                                isMen ? 'text-[#2196F3]' : 
                                'text-[#9C27B0]'
                              }`}>
                                {isWomen ? '♀' : isMen ? '♂' : '⚧'} {segmentData.gender} • {segmentData.age_group}
                              </h4>
                              <ol className="space-y-1">
                                {(segmentData.careers || []).slice(0, 5).map((c, i) => (
                                  <li key={i} className="text-sm text-[#1D1D1D]/80 flex justify-between">
                                    <span className="truncate flex-1">{c.career}</span>
                                    <span className="font-medium ml-2">{c.count}</span>
                                  </li>
                                ))}
                              </ol>
                            </div>
                          );
                        })}
                    </div>
                  </div>

                  {/* By Region breakdown - Belgian regions only */}
                  {(() => {
                    const BELGIAN_REGIONS = {
                      'Brussels Capital': 'Bruxelles-Capitale',
                      'Brussels': 'Bruxelles-Capitale',
                      'Bruxelles': 'Bruxelles-Capitale',
                      'Bruxelles-Capitale': 'Bruxelles-Capitale',
                      'Wallonia': 'Wallonie',
                      'Wallonie': 'Wallonie',
                      'Flanders': 'Flandre',
                      'Flandre': 'Flandre',
                      'Vlaanderen': 'Flandre'
                    };
                    
                    const belgianRegionsData = Object.entries(data.careers_by_gender_age.by_region || {})
                      .filter(([region]) => {
                        if (!region || region.includes('null')) return false;
                        const regionLower = region.toLowerCase();
                        return Object.keys(BELGIAN_REGIONS).some(r => 
                          regionLower.includes(r.toLowerCase()) || r.toLowerCase().includes(regionLower)
                        );
                      })
                      .map(([region, segments]) => {
                        // Normalize region name
                        let normalizedName = region;
                        for (const [key, value] of Object.entries(BELGIAN_REGIONS)) {
                          if (region.toLowerCase().includes(key.toLowerCase())) {
                            normalizedName = value;
                            break;
                          }
                        }
                        return [normalizedName, segments];
                      });
                    
                    if (belgianRegionsData.length === 0) return null;
                    
                    return (
                      <div>
                        <h3 className="font-semibold text-[#1D1D1D] mb-3 flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-[#4CAF50]" />
                          {dashboardLang === 'fr' ? 'Par région (Belgique)' : 'Per regio (België)'}
                        </h3>
                        <div className="space-y-4">
                          {belgianRegionsData.slice(0, 3).map(([region, segments]) => (
                            <div key={region} className="bg-gray-50 rounded-lg p-4">
                              <h4 className="font-medium text-[#4CAF50] mb-3 flex items-center gap-2">
                                <MapPin className="w-4 h-4" /> {region}
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {Object.entries(segments)
                                  .filter(([key]) => key && !key.includes('null'))
                                  .slice(0, 6)
                                  .map(([segKey, segData]) => (
                                    <div key={segKey} className="bg-white rounded p-2 text-xs">
                                      <div className="font-medium text-[#1D1D1D] mb-1">
                                        {segData.gender === 'Femmes' ? '♀' : segData.gender === 'Hommes' ? '♂' : '⚧'} {segData.gender} • {segData.age_group}
                                      </div>
                                      <div className="text-[#1D1D1D]/70">
                                        {(segData.careers || []).slice(0, 2).map((c, i) => (
                                          <div key={i}>{i + 1}. {c.career}</div>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
              
              {!data.careers_by_gender_age && (
                <p className="text-[#1D1D1D]/60 text-sm text-center py-8">
                  {dashboardLang === 'fr' ? 'Données insuffisantes pour l\'analyse par segment' : 'Onvoldoende gegevens voor segmentanalyse'}
                </p>
              )}
            </section>

            {/* Careers by age group */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#A4187A]" />
                {getLabel('charts.careers_by_age')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(data.careers_by_age || {})
                  .filter(([age]) => age && age !== 'null' && age !== 'undefined')
                  .map(([age, careers]) => (
                  <div key={age} className="bg-[#F3E5F5] rounded-lg p-4">
                    <h3 className="font-bold text-[#A4187A] mb-2">{age}</h3>
                    <ol className="space-y-1">
                      {careers.slice(0, 5).map((c, i) => (
                        <li key={i} className="text-sm text-[#1D1D1D]/80 flex justify-between">
                          <span>{i + 1}. {c.career}</span>
                          <span className="text-[#A4187A] font-medium">{c.count}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                ))}
                {Object.entries(data.careers_by_age || {}).filter(([k]) => k && k !== 'null' && k !== 'undefined').length === 0 && (
                  <p className="text-[#1D1D1D]/60 text-sm col-span-4 text-center py-4">Pas assez de données disponibles</p>
                )}
              </div>
            </section>

            {/* Careers by gender */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#E91E63]" />
                {getLabel('charts.careers_by_gender')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(data.careers_by_gender || {})
                  .filter(([gender]) => gender && gender !== 'null' && gender !== 'undefined')
                  .map(([gender, careers]) => (
                  <div key={gender} className={`rounded-lg p-4 ${
                    gender === 'Femmes' ? 'bg-[#FCE4EC]' : 
                    gender === 'Hommes' ? 'bg-[#E3F2FD]' : 'bg-[#F3E5F5]'
                  }`}>
                    <h3 className={`font-bold mb-2 ${
                      gender === 'Femmes' ? 'text-[#E91E63]' : 
                      gender === 'Hommes' ? 'text-[#2196F3]' : 'text-[#9C27B0]'
                    }`}>{gender}</h3>
                    <ol className="space-y-1">
                      {careers.slice(0, 5).map((c, i) => (
                        <li key={i} className="text-sm text-[#1D1D1D]/80 flex justify-between">
                          <span>{i + 1}. {c.career}</span>
                          <span className="font-medium">{c.count}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                ))}
                {Object.entries(data.careers_by_gender || {}).filter(([k]) => k && k !== 'null' && k !== 'undefined').length === 0 && (
                  <p className="text-[#1D1D1D]/60 text-sm col-span-3 text-center py-4">Pas assez de données disponibles</p>
                )}
              </div>
            </section>

            {/* Domains by age group */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <PieChart className="w-5 h-5 text-[#4CAF50]" />
                {getLabel('charts.domains_by_age')}
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E0E0E0]">
                      <th className="text-left py-2 px-3 text-[#1D1D1D]/60">Tranche d'âge</th>
                      <th className="text-left py-2 px-3 text-[#1D1D1D]/60">Top domaines</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(data.domains_by_age || {}).map(([age, domains]) => (
                      <tr key={age} className="border-b border-[#E0E0E0]">
                        <td className="py-3 px-3 font-medium text-[#A4187A]">{age}</td>
                        <td className="py-3 px-3">
                          <div className="flex flex-wrap gap-2">
                            {domains.slice(0, 3).map((d, i) => (
                              <span 
                                key={i} 
                                className="px-2 py-1 rounded-full text-xs text-white"
                                style={{ backgroundColor: DOMAIN_COLORS[d.domain] || COLORS[i] }}
                              >
                                {d.domain} ({d.count})
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Domains by gender */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-[#FB6823]" />
                {getLabel('charts.domains_by_gender')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(data.domains_by_gender || {}).map(([gender, domains]) => (
                  <div key={gender} className="bg-[#FFF3E0] rounded-lg p-4">
                    <h3 className="font-bold text-[#FB6823] mb-3">{gender}</h3>
                    <div className="space-y-2">
                      {domains.slice(0, 5).map((d, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: DOMAIN_COLORS[d.domain] || COLORS[i] }}
                          />
                          <span className="text-sm text-[#1D1D1D]/80 flex-1">{d.domain}</span>
                          <span className="text-sm font-medium">{d.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* PART 5 & 6: Advanced Cross-Analysis Table */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#FB6823]" />
                {dashboardLang === 'fr' ? 'Analyses croisées avancées' : 'Geavanceerde kruisanalyse'}
              </h2>
              <p className="text-sm text-[#1D1D1D]/60 mb-4">
                {dashboardLang === 'fr' 
                  ? 'Vue consolidée des données démographiques × carrières × domaines'
                  : 'Geconsolideerde weergave van demografische gegevens × carrières × domeinen'
                }
              </p>
              
              {/* Gender x Domain Matrix */}
              <div className="mb-6">
                <h3 className="font-semibold text-[#1D1D1D] mb-3">
                  {dashboardLang === 'fr' ? 'Domaines préférés par genre' : 'Voorkeurdomeinen per geslacht'}
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200">
                    <thead>
                      <tr className="bg-gradient-to-r from-[#E91E63]/10 to-[#2196F3]/10">
                        <th className="p-3 text-left font-semibold border-b">
                          {dashboardLang === 'fr' ? 'Domaine' : 'Domein'}
                        </th>
                        <th className="p-3 text-center font-semibold border-b text-[#E91E63]">
                          {dashboardLang === 'fr' ? 'Femmes' : 'Vrouwen'}
                        </th>
                        <th className="p-3 text-center font-semibold border-b text-[#2196F3]">
                          {dashboardLang === 'fr' ? 'Hommes' : 'Mannen'}
                        </th>
                        <th className="p-3 text-center font-semibold border-b text-[#9C27B0]">
                          {dashboardLang === 'fr' ? 'Autre' : 'Anders'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Get all unique domains from all genders
                        const allDomains = new Set();
                        const femaleData = data.domains_by_gender?.Femmes || [];
                        const maleData = data.domains_by_gender?.Hommes || [];
                        const otherData = data.domains_by_gender?.Autre || [];
                        
                        femaleData.forEach(d => allDomains.add(d.domain));
                        maleData.forEach(d => allDomains.add(d.domain));
                        otherData.forEach(d => allDomains.add(d.domain));
                        
                        // Calculate totals for percentages
                        const femaleTotal = femaleData.reduce((sum, d) => sum + d.count, 0);
                        const maleTotal = maleData.reduce((sum, d) => sum + d.count, 0);
                        const otherTotal = otherData.reduce((sum, d) => sum + d.count, 0);
                        
                        // Sort domains by total count
                        const sortedDomains = Array.from(allDomains).map(domain => {
                          const female = femaleData.find(d => d.domain === domain)?.count || 0;
                          const male = maleData.find(d => d.domain === domain)?.count || 0;
                          const other = otherData.find(d => d.domain === domain)?.count || 0;
                          return { domain, female, male, other, total: female + male + other };
                        }).sort((a, b) => b.total - a.total).slice(0, 8);
                        
                        if (sortedDomains.length === 0) {
                          return (
                            <tr>
                              <td colSpan={4} className="p-4 text-center text-[#1D1D1D]/50">
                                {dashboardLang === 'fr' ? 'Aucune donnée disponible' : 'Geen gegevens beschikbaar'}
                              </td>
                            </tr>
                          );
                        }
                        
                        return sortedDomains.map((item, idx) => {
                          const femalePct = femaleTotal > 0 ? ((item.female / femaleTotal) * 100).toFixed(1) : '0.0';
                          const malePct = maleTotal > 0 ? ((item.male / maleTotal) * 100).toFixed(1) : '0.0';
                          const otherPct = otherTotal > 0 ? ((item.other / otherTotal) * 100).toFixed(1) : '0.0';
                          
                          return (
                            <tr key={item.domain} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                              <td className="p-3 border-b font-medium">{item.domain}</td>
                              <td className="p-3 border-b text-center text-[#E91E63]">
                                {item.female > 0 ? `${femalePct}%` : '-'}
                              </td>
                              <td className="p-3 border-b text-center text-[#2196F3]">
                                {item.male > 0 ? `${malePct}%` : '-'}
                              </td>
                              <td className="p-3 border-b text-center text-[#9C27B0]">
                                {item.other > 0 ? `${otherPct}%` : '-'}
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Insights Box */}
              <div className="bg-gradient-to-br from-[#FFF8E1] to-[#FFF3E0] rounded-lg p-4 border border-[#FFE0B2]">
                <h4 className="font-semibold text-[#FF6F00] mb-2 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4" />
                  {dashboardLang === 'fr' ? 'Insights clés' : 'Belangrijke inzichten'}
                </h4>
                <ul className="text-sm text-[#1D1D1D]/80 space-y-1">
                  <li>• {dashboardLang === 'fr' 
                    ? `Domaine le plus populaire tous genres confondus: ${data.domain_distribution?.[0]?.domain || 'N/A'}`
                    : `Populairste domein over alle geslachten: ${data.domain_distribution?.[0]?.domain || 'N/A'}`
                  }</li>
                  <li>• {dashboardLang === 'fr'
                    ? `Tranche d'âge la plus active: ${Object.entries(data.demographics?.age_distribution || {}).sort((a,b) => b[1] - a[1])[0]?.[0] || 'N/A'}`
                    : `Meest actieve leeftijdsgroep: ${Object.entries(data.demographics?.age_distribution || {}).sort((a,b) => b[1] - a[1])[0]?.[0] || 'N/A'}`
                  }</li>
                  <li>• {dashboardLang === 'fr'
                    ? `Ratio H/F: ${data.demographics?.gender_distribution?.['Hommes'] || 0}/${data.demographics?.gender_distribution?.['Femmes'] || 0}`
                    : `M/V ratio: ${data.demographics?.gender_distribution?.['Hommes'] || 0}/${data.demographics?.gender_distribution?.['Femmes'] || 0}`
                  }</li>
                </ul>
              </div>
            </section>
          </>
        )}

        {/* Geography Tab */}
        {activeTab === 'geography' && data && (
          <>
            {/* Interactive Belgium Map */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#4CAF50]" />
                {getLabel('charts.interactive_map')}
              </h2>
              <p className="text-sm text-[#1D1D1D]/60 mb-4">
                Visualisation géographique des utilisateurs par ville et province. Survolez les provinces pour plus de détails.
              </p>
              <div className="h-[450px] bg-[#F8F9FA] rounded-lg overflow-hidden">
                <BelgiumMap 
                  data={data.map_data || {}}
                  mode="users"
                  onProvinceClick={(province) => console.log('Province clicked:', province)}
                />
              </div>
            </section>

            {/* Cities and municipalities rankings side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
                <h3 className="font-medium text-[#1D1D1D] mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4 text-[#4CAF50]" />
                  {getLabel('charts.top_cities')}
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart 
                    data={Object.entries(data.map_data?.by_city || {})
                      .filter(([k]) => k && k !== 'null')
                      .map(([city, count]) => ({ city, count }))
                      .sort((a, b) => b.count - a.count)
                      .slice(0, 10)
                    }
                    layout="vertical"
                    margin={{ left: 100 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis dataKey="city" type="category" tick={{ fontSize: 11 }} width={90} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#4CAF50" radius={[0, 4, 4, 0]} name="Utilisateurs" />
                  </BarChart>
                </ResponsiveContainer>
              </section>

              <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
                <h3 className="font-medium text-[#1D1D1D] mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#FB6823]" />
                  {getLabel('charts.top_municipalities')}
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart 
                    data={Object.entries(data.map_data?.by_municipality || {})
                      .filter(([k]) => k && k !== 'null')
                      .map(([municipality, count]) => ({ municipality, count }))
                      .sort((a, b) => b.count - a.count)
                      .slice(0, 10)
                    }
                    layout="vertical"
                    margin={{ left: 100 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis dataKey="municipality" type="category" tick={{ fontSize: 11 }} width={90} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#FB6823" radius={[0, 4, 4, 0]} name="Utilisateurs" />
                  </BarChart>
                </ResponsiveContainer>
              </section>
            </div>

            {/* Careers and domains by region */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
                <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-[#A4187A]" />
                  {getLabel('charts.careers_by_region')}
                </h2>
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {Object.entries(data.map_data?.careers_by_region || {})
                    .filter(([k]) => k && k !== 'null')
                    .map(([region, info]) => (
                    <div key={region} className="bg-gradient-to-r from-[#F3E5F5] to-[#E8EAF6] rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <span className="font-bold text-[#A4187A] text-sm">{region}</span>
                        <p className="text-[#1D1D1D]/80 text-sm">{info.career}</p>
                      </div>
                      <span className="bg-[#A4187A] text-white text-xs px-2 py-1 rounded-full">
                        {info.count}
                      </span>
                    </div>
                  ))}
                  {Object.keys(data.map_data?.careers_by_region || {}).filter(k => k && k !== 'null').length === 0 && (
                    <p className="text-[#1D1D1D]/60 text-sm text-center py-4">Pas assez de données disponibles</p>
                  )}
                </div>
              </section>

              <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
                <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-[#2196F3]" />
                  {getLabel('charts.domains_by_region')}
                </h2>
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {Object.entries(data.map_data?.domains_by_region || {})
                    .filter(([k]) => k && k !== 'null')
                    .map(([region, info]) => (
                    <div key={region} className="bg-gradient-to-r from-[#E3F2FD] to-[#E0F7FA] rounded-lg p-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: DOMAIN_COLORS[info.domain] || '#A4187A' }}
                        />
                        <div>
                          <span className="font-bold text-[#2196F3] text-sm">{region}</span>
                          <p className="text-[#1D1D1D]/80 text-sm">{info.domain}</p>
                        </div>
                      </div>
                      <span className="bg-[#2196F3] text-white text-xs px-2 py-1 rounded-full">
                        {info.count}
                      </span>
                    </div>
                  ))}
                  {Object.keys(data.map_data?.domains_by_region || {}).filter(k => k && k !== 'null').length === 0 && (
                    <p className="text-[#1D1D1D]/60 text-sm text-center py-4">Pas assez de données disponibles</p>
                  )}
                </div>
              </section>
            </div>

            {/* Geographic summary stats */}
            <section className="bg-gradient-to-r from-[#4CAF50]/10 to-[#2196F3]/10 rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4">{getLabel('charts.geographic_summary')}</h2>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-white rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-[#4CAF50]">
                    {data.geolocation?.by_country?.['Belgique'] || 0}
                  </div>
                  <div className="text-xs text-[#1D1D1D]/60">{getLabel('charts.belgian_users')}</div>
                </div>
                <div className="bg-white rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-[#9E9E9E]">
                    {data.geolocation?.by_country?.['Reste du monde'] || data.map_data?.rest_of_world_count || 0}
                  </div>
                  <div className="text-xs text-[#1D1D1D]/60">{getLabel('charts.rest_of_world')}</div>
                </div>
                <div className="bg-white rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-[#FB6823]">
                    {Object.keys(data.map_data?.by_city || {}).filter(k => k && k !== 'null').length}
                  </div>
                  <div className="text-xs text-[#1D1D1D]/60">{getLabel('charts.distinct_cities')}</div>
                </div>
                <div className="bg-white rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-[#A4187A]">
                    {data.geolocation?.declaration_rate || 0}%
                  </div>
                  <div className="text-xs text-[#1D1D1D]/60">{getLabel('charts.declaration_rate')}</div>
                </div>
                <div className="bg-white rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-[#2196F3]">
                    {Object.keys(data.map_data?.careers_by_region || {}).filter(k => k && k !== 'null').length}
                  </div>
                  <div className="text-xs text-[#1D1D1D]/60">{getLabel('charts.regions_covered')}</div>
                </div>
              </div>
            </section>
          </>
        )}

        {/* User Results Tab */}
        {activeTab === 'results' && (
          <UserResultsTab dashboardLang={dashboardLang} API={API} />
        )}

        {/* Insights Tab */}
        {activeTab === 'insights' && data && (
          <>
            {/* Link to full Advocacy Page */}
            <section className="bg-gradient-to-r from-[#FB6823] to-[#FFC107] rounded-xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
                    <FileText className="w-6 h-6" />
                    {dashboardLang === 'fr' ? 'Plaidoyer Déclic' : 'Déclic Pleidooi'}
                  </h2>
                  <p className="text-white/80 text-sm">
                    {dashboardLang === 'fr' 
                      ? 'Accédez à la page complète de plaidoyer avec analyse détaillée, recommandations et export PDF pour vos partenaires.'
                      : 'Toegang tot de volledige advocacy-pagina met gedetailleerde analyse, aanbevelingen en PDF-export voor uw partners.'
                    }
                  </p>
                </div>
                <a
                  href="/plaidoyer"
                  className="px-6 py-3 bg-white text-[#FB6823] rounded-lg font-semibold hover:bg-white/90 transition-colors flex items-center gap-2"
                  data-testid="link-to-advocacy"
                >
                  <ExternalLink className="w-5 h-5" />
                  {dashboardLang === 'fr' ? 'Voir le plaidoyer' : 'Bekijk pleidooi'}
                </a>
              </div>
            </section>

            {/* Key Findings - Points clés à retenir */}
            {data.key_findings && data.key_findings.length > 0 && (
              <section className="bg-white rounded-xl p-6 border-2 border-[#FB6823]">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-[#FB6823]">
                  <Lightbulb className="w-6 h-6" />
                  {dashboardLang === 'fr' ? 'Points clés à retenir' : 'Belangrijkste punten'}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {data.key_findings.slice(0, 5).map((finding, idx) => {
                    const severityStyles = {
                      success: { bg: '#E8F5E9', border: '#4CAF50', text: '#2E7D32' },
                      warning: { bg: '#FFF3E0', border: '#FF9800', text: '#E65100' },
                      info: { bg: '#E3F2FD', border: '#2196F3', text: '#1565C0' }
                    };
                    const style = severityStyles[finding.severity] || severityStyles.info;
                    return (
                      <div 
                        key={idx}
                        className="p-4 rounded-lg border-l-4"
                        style={{ backgroundColor: style.bg, borderColor: style.border }}
                      >
                        <h3 className="font-semibold" style={{ color: style.text }}>
                          {finding.title}
                        </h3>
                        <p className="text-sm text-[#1D1D1D]/70 mt-1">{finding.text}</p>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-[#1D1D1D]">
                <Lightbulb className="w-6 h-6 text-[#A4187A]" />
                {getLabel('interpretations.title')}
              </h2>
              <p className="text-[#1D1D1D]/60 text-sm mb-6">
                {getLabel('interpretations.subtitle')}
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(data.interpretations || []).map((insight, i) => (
                  <div key={i} className="bg-gradient-to-r from-[#F3E5F5] to-[#E8EAF6] rounded-lg p-4 border border-[#A4187A]/10">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#A4187A]/10 flex items-center justify-center flex-shrink-0">
                        {insight.icon === 'users' && <Users className="w-5 h-5 text-[#A4187A]" />}
                        {insight.icon === 'pie-chart' && <PieChart className="w-5 h-5 text-[#A4187A]" />}
                        {insight.icon === 'trending-up' && <TrendingUp className="w-5 h-5 text-[#FB6823]" />}
                        {insight.icon === 'lightbulb' && <Lightbulb className="w-5 h-5 text-[#FFC107]" />}
                        {insight.icon === 'check-circle' && <CheckCircle className="w-5 h-5 text-[#4CAF50]" />}
                        {insight.icon === 'map-pin' && <MapPin className="w-5 h-5 text-[#2196F3]" />}
                      </div>
                      <div>
                        <span className="text-xs text-[#A4187A] uppercase tracking-wider font-medium">
                          {insight.category}
                        </span>
                        <p className="text-[#1D1D1D]/80 mt-1">{insight.text}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Summary stats */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4">{getLabel('charts.key_indicators')}</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-[#F3E5F5] rounded-lg">
                  <div className="text-3xl font-bold text-[#A4187A]">
                    {data.geolocation?.total_sessions || 0}
                  </div>
                  <div className="text-sm text-[#1D1D1D]/60">{getLabel('charts.total_sessions')}</div>
                </div>
                <div className="text-center p-4 bg-[#E8F5E9] rounded-lg">
                  <div className="text-3xl font-bold text-[#4CAF50]">
                    {data.geolocation?.declaration_rate || 0}%
                  </div>
                  <div className="text-sm text-[#1D1D1D]/60">{getLabel('charts.declaration_rate')}</div>
                </div>
                <div className="text-center p-4 bg-[#FFF3E0] rounded-lg">
                  <div className="text-3xl font-bold text-[#FB6823]">
                    {(data.domain_distribution || []).length}
                  </div>
                  <div className="text-sm text-[#1D1D1D]/60">{getLabel('charts.domains_represented')}</div>
                </div>
                <div className="text-center p-4 bg-[#E3F2FD] rounded-lg">
                  <div className="text-3xl font-bold text-[#2196F3]">
                    {(data.career_popularity?.most_suggested || []).length}
                  </div>
                  <div className="text-sm text-[#1D1D1D]/60">{getLabel('charts.suggested_careers')}</div>
                </div>
              </div>
            </section>

            {/* Data quality */}
            <section className="bg-[#FFF3E0] rounded-xl p-6 border border-[#FB6823]/20">
              <h2 className="text-lg font-bold text-[#FB6823] mb-2">{getLabel('charts.data_quality')}</h2>
              <p className="text-sm text-[#1D1D1D]/70 mb-4">
                {getLabel('charts.data_quality_note')}
              </p>
              <div className="flex flex-wrap gap-4 text-sm">
                <span className="px-3 py-1 bg-white rounded-full text-[#1D1D1D]/70">
                  {getLabel('charts.last_update')} : {data.generated_at ? new Date(data.generated_at).toLocaleString(dashboardLang === 'nl' ? 'nl-BE' : 'fr-BE') : 'N/A'}
                </span>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

// Helper components
function MetricCard({ icon: Icon, label, value, color, subtitle }) {
  return (
    <div className="bg-white rounded-xl p-4 border border-[#E0E0E0]">
      <div className="flex items-center gap-3">
        <div 
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${color}20` }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div>
          <p className="text-2xl font-bold text-[#1D1D1D]">{value}</p>
          <p className="text-xs text-[#1D1D1D]/60">{label}</p>
          {subtitle && <p className="text-[10px] text-[#1D1D1D]/40 mt-0.5">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
}

function formatGender(key) {
  const labels = {
    male: "Hommes",
    female: "Femmes",
    other: "Autre",
    prefer_not: "Non spécifié"
  };
  return labels[key] || key;
}

// ============================================================
// USER RESULTS TAB COMPONENT
// ============================================================

function UserResultsTab({ dashboardLang, API }) {
  const [results, setResults] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchResults();
    fetchStats();
  }, [page]);

  const fetchResults = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/user-results?page=${page}&per_page=15`);
      setResults(response.data.results || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching user results:', err);
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/user-results/stats/summary`);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const exportCSV = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/user-results/export/csv`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `declic-user-results-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV export error:', err);
      alert(dashboardLang === 'fr' ? 'Erreur lors de l\'export' : 'Export fout');
    }
    setExporting(false);
  };

  return (
    <>
      {/* Stats Cards */}
      {stats && (
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-[#E0E0E0] text-center">
            <div className="text-3xl font-bold text-[#A4187A]">{stats.total}</div>
            <div className="text-sm text-[#1D1D1D]/60">
              {dashboardLang === 'fr' ? 'Résultats sauvegardés' : 'Opgeslagen resultaten'}
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-[#E0E0E0] text-center">
            <div className="text-3xl font-bold text-[#4CAF50]">{stats.stage1_completion_rate}%</div>
            <div className="text-sm text-[#1D1D1D]/60">
              {dashboardLang === 'fr' ? 'Étape 1 complétée' : 'Stap 1 voltooid'}
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-[#E0E0E0] text-center">
            <div className="text-3xl font-bold text-[#FB6823]">{stats.stage2_completion_rate}%</div>
            <div className="text-sm text-[#1D1D1D]/60">
              {dashboardLang === 'fr' ? 'Étape 2 complétée' : 'Stap 2 voltooid'}
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-[#E0E0E0] text-center">
            <div className="text-3xl font-bold text-[#2196F3]">{stats.avg_feedback_rating || '-'}</div>
            <div className="text-sm text-[#1D1D1D]/60">
              {dashboardLang === 'fr' ? 'Note moyenne feedback' : 'Gemiddelde feedback'}
            </div>
          </div>
        </section>
      )}

      {/* Results Table */}
      <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-[#1D1D1D] flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#A4187A]" />
            {dashboardLang === 'fr' ? 'Résultats utilisateurs' : 'Gebruikersresultaten'}
            <span className="text-sm font-normal text-[#1D1D1D]/60">({total} total)</span>
          </h2>
          <button
            onClick={exportCSV}
            disabled={exporting}
            className="px-4 py-2 bg-[#A4187A] text-white rounded-lg hover:bg-[#8E1569] transition-colors flex items-center gap-2"
            data-testid="export-results-csv"
          >
            <Download className={`w-4 h-4 ${exporting ? 'animate-bounce' : ''}`} />
            {dashboardLang === 'fr' ? 'Export CSV' : 'CSV Exporteren'}
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <RefreshCw className="w-8 h-8 text-[#A4187A] animate-spin mx-auto" />
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-8 text-[#1D1D1D]/60">
            {dashboardLang === 'fr' 
              ? 'Aucun résultat sauvegardé pour le moment.' 
              : 'Nog geen opgeslagen resultaten.'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#A4187A]/10">
                    <th className="p-3 text-left font-semibold">{dashboardLang === 'fr' ? 'Date' : 'Datum'}</th>
                    <th className="p-3 text-left font-semibold">{dashboardLang === 'fr' ? 'Langue' : 'Taal'}</th>
                    <th className="p-3 text-left font-semibold">{dashboardLang === 'fr' ? 'Âge' : 'Leeftijd'}</th>
                    <th className="p-3 text-left font-semibold">{dashboardLang === 'fr' ? 'Genre' : 'Geslacht'}</th>
                    <th className="p-3 text-left font-semibold">{dashboardLang === 'fr' ? 'Région' : 'Regio'}</th>
                    <th className="p-3 text-center font-semibold">{dashboardLang === 'fr' ? 'Étape 1' : 'Stap 1'}</th>
                    <th className="p-3 text-center font-semibold">{dashboardLang === 'fr' ? 'Étape 2' : 'Stap 2'}</th>
                    <th className="p-3 text-left font-semibold">{dashboardLang === 'fr' ? 'Top domaines' : 'Top domeinen'}</th>
                    <th className="p-3 text-left font-semibold">{dashboardLang === 'fr' ? 'Métier top' : 'Top beroep'}</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((result, idx) => (
                    <tr key={result.session_id || idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="p-3 border-b border-gray-100 text-xs">
                        {result.created_at ? new Date(result.created_at).toLocaleDateString('fr-FR') : '-'}
                      </td>
                      <td className="p-3 border-b border-gray-100">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          result.language === 'fr' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                        }`}>
                          {result.language?.toUpperCase() || '-'}
                        </span>
                      </td>
                      <td className="p-3 border-b border-gray-100">{result.age_group || '-'}</td>
                      <td className="p-3 border-b border-gray-100">{formatGender(result.gender) || '-'}</td>
                      <td className="p-3 border-b border-gray-100 text-xs">{result.region || result.municipality || '-'}</td>
                      <td className="p-3 border-b border-gray-100 text-center">
                        {result.stage1_completed ? (
                          <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="p-3 border-b border-gray-100 text-center">
                        {result.stage2_completed ? (
                          <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="p-3 border-b border-gray-100">
                        <div className="flex flex-wrap gap-1">
                          {(result.top_domains || []).slice(0, 2).map((domain, i) => (
                            <span 
                              key={i}
                              className="px-2 py-0.5 bg-[#F3E5F5] text-[#A4187A] rounded text-xs"
                            >
                              {domain}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="p-3 border-b border-gray-100 text-xs max-w-[150px] truncate" title={result.stage2_top_career}>
                        {result.stage2_top_career || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="text-sm text-[#1D1D1D]/60">
                  Page {page} / {totalPages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {dashboardLang === 'fr' ? 'Précédent' : 'Vorige'}
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {dashboardLang === 'fr' ? 'Suivant' : 'Volgende'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}

